import {useEffect, useMemo} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {
  RampIdType,
  useViewerOrganizationFeatureFlagsQuery,
} from '../../../generated/graphql';
import {type OrgFlagMaps, toOrgFlagMaps} from './flagMap';

/**
 * Caches `useViewerOrganizationFeatureFlagsQuery` results as precomputed
 * `orgId -> rampKey -> boolean` (for orgId-scoped lookups) plus an
 * `anyOrgEnabledMap` `rampKey -> boolean | undefined` map (matches the
 * legacy "first truthy org wins, else last seen value" loop in
 * `useOrgRampFlagWithLoading`). Readers are a single Record lookup.
 */
export type ViewerOrgFlagsState = {
  flagsByOrgId: OrgFlagMaps | null;
  flagsByOrgName: OrgFlagMaps | null;
};

const INITIAL_VIEWER_ORG_FLAGS_STATE: ViewerOrgFlagsState = {
  flagsByOrgId: null,
  flagsByOrgName: null,
};

// Reader hooks live in `useRampFlagStore.ts`; export the store so they can
// subscribe to it directly.
export const useViewerOrgFlagsStore = create<ViewerOrgFlagsState>()(
  devtools(() => INITIAL_VIEWER_ORG_FLAGS_STATE, {name: 'ViewerOrgFlagsStore'})
);

export function useInitializeViewerOrgFlagsStore() {
  const {data: dataOrgId} = useViewerOrganizationFeatureFlagsQuery({
    variables: {rampIDType: RampIdType.OrgId},
  });
  const {data: dataOrgName} = useViewerOrganizationFeatureFlagsQuery({
    variables: {rampIDType: RampIdType.OrgName},
  });

  const flagsByOrgId = useMemo(
    () => toOrgFlagMaps(dataOrgId?.viewer?.organizations ?? null),
    [dataOrgId]
  );
  const flagsByOrgName = useMemo(
    () => toOrgFlagMaps(dataOrgName?.viewer?.organizations ?? null),
    [dataOrgName]
  );

  // This is a valid Effect: it synchronizes React hook outputs into the
  // external Zustand store after render.
  // https://react.dev/reference/react/useEffect
  useEffect(() => {
    useViewerOrgFlagsStore.setState({flagsByOrgId, flagsByOrgName});
  }, [flagsByOrgId, flagsByOrgName]);
}
